import { useCallback, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useUploadMutation } from "@/hooks/uploads/useUploadMutation";
import {
  validateAudioFile,
  validateUploadFileSize,
  getErrorMessage,
  getTooLargeMessage,
  formatFileSize,
} from "@/hooks/uploads/uploadValidation";
import { uploadFileToStorage } from "@/lib/uploadFileToStorage";
import AgentSelection from "../AgentSelection";
import { UploadComponentProps } from "./types";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface UploadAudioProps extends UploadComponentProps {
  audioFile: File | null;
  onAudioFileChange: (file: File | null) => void;
}

const UploadAudio = ({
  availableAgents,
  selectedAgentId,
  onAgentSelect,
  audioFile,
  onAudioFileChange,
  onUploadSuccess,
  onDeferredActionAdd,
}: UploadAudioProps) => {
  const audioTranscribeMutation = useUploadMutation("audio", selectedAgentId, onUploadSuccess);
  const [isUploadingAudio, setIsUploadingAudio] = useState(false);

  const uploadAudioFile = useCallback(
    async (file: File) => {
      try {
        setIsUploadingAudio(true);
        const uploadToast = toast.loading(`Uploading "${file.name}"...`);

        const { fileUrl } = await uploadFileToStorage(file, "temp-uploads");

        toast.dismiss(uploadToast);

        onDeferredActionAdd({
          type: "audio",
          data: {
            fileUrl,
            fileName: file.name,
            language: "en",
          },
        });
        toast.success(`"${file.name}" uploaded and queued for transcription`);
        onAudioFileChange(null);
      } catch (error) {
        toast.error(`Failed to upload "${file.name}"`);
        console.error("Upload error:", error);
      } finally {
        setIsUploadingAudio(false);
      }
    },
    [onDeferredActionAdd, onAudioFileChange],
  );

  const handleAudioFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!validateAudioFile(file)) {
        toast.error(getErrorMessage("audio"));
        return;
      }

      if (!validateUploadFileSize(file)) {
        toast.error(getTooLargeMessage(file));
        return;
      }

      if (onDeferredActionAdd) {
        await uploadAudioFile(file);
        e.target.value = "";
      } else {
        onAudioFileChange(file);
      }
    },
    [onAudioFileChange, onDeferredActionAdd, uploadAudioFile],
  );

  return (
    <>
      <AgentSelection
        agents={availableAgents}
        selectedAgentId={selectedAgentId}
        onAgentSelect={onAgentSelect}
      />
      <div className="flex flex-col items-center justify-center h-full max-w-2xl mx-auto">
        <GenieIcon name="microphone" size={64} color="#4E9CFF" gradientEnabled className="mb-4" />
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Transcribe Audio File</h3>
        <p className="text-sm text-gray-600 mb-6 text-center">Upload an audio file to transcribe to text</p>

        <input
          type="file"
          accept="audio/*"
          onChange={handleAudioFileChange}
          className="hidden"
          id="audio-file-input"
        />
        <label
          htmlFor="audio-file-input"
          className="flex items-center justify-center w-full max-w-md h-32 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-genie-primary transition-colors bg-gray-50"
        >
          {audioFile ? (
            <div className="text-center">
              <p className="text-sm font-medium text-gray-700">{audioFile.name}</p>
              <p className="text-xs text-gray-500 mt-1">{formatFileSize(audioFile.size)}</p>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-sm text-gray-600">Click to upload audio file</p>
              <p className="text-xs text-gray-500 mt-1">MP3, WAV, M4A, OGG, or WebM</p>
            </div>
          )}
        </label>
        {audioFile && (
          <Button
            onClick={() => {
              if (onDeferredActionAdd) {
                uploadAudioFile(audioFile);
              } else {
                audioTranscribeMutation.mutate({ file: audioFile });
              }
            }}
            disabled={!audioFile || audioTranscribeMutation.isPending || isUploadingAudio}
            className="mt-4 bg-gradient-to-r from-genie-primary to-genie-secondary text-white font-semibold px-8 h-12 shadow-lg hover:shadow-xl transition-all hover:scale-105 rounded-xl"
          >
            {audioTranscribeMutation.isPending || isUploadingAudio ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                {isUploadingAudio ? "Uploading..." : "Transcribing..."}
              </>
            ) : (
              onDeferredActionAdd ? "Upload Audio" : "Transcribe Audio"
            )}
          </Button>
        )}
      </div>
    </>
  );
};

export default UploadAudio;
